import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Scrollbar } from 'react-scrollbars-custom';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useAccessToken } from '../../../common/hooks/useAccessToken';
import useBodyClass from '../../../common/hooks/useBodyClassName';
import PrintPdfDropdown from '../../../common/export_documents/PrintPdfDropdown';
import InputGroup from '../../../common/InputGroup';
import DateMonthPicker from '../../../common/DatepickerPortal';
import Switch from '../../../common/Switch';
import Input from '../../../common/simple/input';
import BllinkTime from '../../../common/utils/time';
import Button from '../../../../components/common/Button';
import { isAdminSelector } from '../../../store/auth/selectors';
import { hideLoader, showLoader } from '../../../store/common/slice';
import { getBuildingTitle } from '../../../store/buildings/actions';
import Search from '../../../images/search.svg';
import refresh from '../../../images/newDesign/refresh.svg';
import { StyledReport } from '../report.styled';
import { StyledBalanceReport } from './BalanceReport.styled';
import BalanceReportCard from './BalanceReportCard';
import { Currency } from "../../../common/currency";
import Money from "../../../common/utils/money";
import StringUtils from "../../../common/utils/strings";
import { NotificationManager } from 'react-notifications';
import { PageTitle } from '../../../common/PageTitle';

const BalanceReport = ({ match }) => {
    const { buildingID } = match.params;
    const isAdmin = useSelector(isAdminSelector);
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const client = useAccessToken();
    const titleData = useSelector(state => state.buildings.titleData);
    const ltr = useSelector(state => state.common.ltrEnabled);
    const pdfContainer = useRef();

    const [filter, setFilter] = useState('');
    const [openBankAmount, setOpenBankAmount] = useState(0);
    const [checkboxChecked, setCheckboxChecked] = useState(false);
    const [expenseRecords, setExpenseRecords] = useState([]);
    const [incomeRecords, setIncomeRecords] = useState([]);
    const [toYear, setToYear] = useState([]);
    const [totalRecords, setTotalRecords] = useState([]);
    const [startDate, setStartDate] = useState(new Date());
    const [endDate, setEndDate] = useState(new Date());
    const [firstMonth, setFirstMonth] = useState('');

    useBodyClass('report');

    const loadRecords = async (start, end) => {
        const fromYear = start.getFullYear(),
            fromMonth = start.getMonth() + 1,
            toYear = end.getFullYear(),
            toMonth = end.getMonth() + 1;

        const response = await client.managers.reports.getBalanceForRange(buildingID, fromYear, fromMonth, toYear, toMonth);

        setToYear(toYear);
        setTotalRecords(response?.total);
        setIncomeRecords(response?.incomes);
        setExpenseRecords(response?.expenses);
    };

    useEffect(async () => {
        if (!client) return;

        dispatch(showLoader());

        if (!titleData.translationKey) dispatch(getBuildingTitle({buildingID}));
        await loadRecords(startDate, endDate);

        dispatch(hideLoader());
    }, [client]);

    useEffect(() => {
        const splitDate = BllinkTime.splitDate(startDate);
        let firstMonth = splitDate[1];
        if (firstMonth[0] === '0') firstMonth = firstMonth.slice(1);
        setFirstMonth(t(`common.months.${firstMonth}`));
    }, [startDate]);

    // FIXME: it's not user friendly - we are fetching data on every date update
    useEffect(async () => {
        if (!client) return;

        dispatch(showLoader());

        if (startDate && endDate) await loadRecords(startDate, endDate);
        dispatch(hideLoader());
    }, [startDate, endDate]);

    const onCheckboxChange = () => setCheckboxChecked(!checkboxChecked);

    // Requested by Rayee. Start/end dates must have the same year
    const onStartDateChange = date => {
        setStartDate(date);

        const anotherDate = new Date(endDate);
        if (anotherDate.getFullYear() !== date.getFullYear()) {
            anotherDate.setFullYear(date.getFullYear());
            NotificationManager.warning(t('reports.dateRangeSameYear'), '', 5000);
            if (date.getTime() > anotherDate.getTime()) {
                setEndDate(new Date(date));
            } else {
                setEndDate(anotherDate);
            }
        }
    };

    const onEndDateChange = date => {
        setEndDate(date);

        const anotherDate = new Date(endDate);
        if (anotherDate.getFullYear() !== date.getFullYear()) {
            anotherDate.setFullYear(date.getFullYear());
            NotificationManager.warning(t('reports.dateRangeSameYear'), '', 5000);
            if (date.getTime() < anotherDate.getTime()) {
                setStartDate(new Date(date));
            } else {
                setStartDate(anotherDate);
            }
        }
    };

    const roundValues = value => {
        const rounded = Money.roundNumber(value, 2);
        return StringUtils.numberWithCommas(rounded);
    };

    const renderColumnTitles = income => {
        return (
            <div className='columns-titles'>
                <span>{income ? t('expenses.income_type') : t('expenses.expense_type')}</span>
                <OverlayTrigger
                    placement="top"
                    overlay={props => (
                        <Tooltip {...props}>
                            {t('reports.total_needed_tooltip')} 
                        </Tooltip>
                    )}
                >
                    <span>{t('expenses.total_needed')}</span>
                </OverlayTrigger>
                <OverlayTrigger
                    placement="top"
                    overlay={props => (
                        <Tooltip {...props}>
                            {t('reports.total_charged_tooltip')} 
                        </Tooltip>
                    )}
                >
                    <span>{t('reports.total_charged_balance')}</span>
                </OverlayTrigger>
                <OverlayTrigger
                    placement="top"
                    overlay={props => (
                        <Tooltip {...props}>
                            {t('reports.balance_diff_tooltip')} 
                        </Tooltip>
                    )}
                >
                    <span>{t('reports.balance_diff')}</span>
                </OverlayTrigger>
            </div>
        )
    }

    return (
        <StyledBalanceReport ltr={ltr}>
            <PageTitle text={t('reports.balance_report_title')} subtitle={t('reports.balance_report_subtitle')}>
                <PrintPdfDropdown
                    classes='report-pdf-icons'
                    buildingID={buildingID}
                    eventLabel="balanceReport"
                    titleString={t('reports.balance_title')}
                    reportName={t('reports.balance_report_title')}
                    pdfContainer={pdfContainer.current}
                    disablePdf={true}
                />
            </PageTitle>
            <StyledReport className='report' ltr={ltr}>
                <div className='report-wrap'>
                    <div className='report-top'>
                        <div className='report-top-start'>
                            <div className="search">
                                <input
                                    type="text"
                                    name="search"
                                    onChange={e => setFilter(e.target.value.toLowerCase())}
                                />
                                <img src={Search} alt={t('search.search')} />
                            </div>
                        </div>

                        <div className='datepickers-container'>
                            <InputGroup
                                labelText={`${t('expenses.balance_report.fromDate')}`}
                                labelFor='from'
                                labelClassName='datepicker-label'
                            >
                                <DateMonthPicker
                                    date={startDate}
                                    onChange={onStartDateChange}
                                    id='from'
                                    maxDate={endDate}
                                    language={i18n.language}
                                    className='one-line-design month-year-calendar'
                                    showMonthYear
                                    withPortal={false}
                                />
                            </InputGroup>

                            <InputGroup
                                labelText={`${t('expenses.balance_report.toDate')}`}
                                labelFor='to'
                                labelClassName='datepicker-label'
                            >
                                <DateMonthPicker
                                    date={endDate}
                                    onChange={onEndDateChange}
                                    id='to'
                                    minDate={startDate}
                                    language={i18n.language}
                                    className='one-line-design month-year-calendar'
                                    showMonthYear
                                    withPortal={false}
                                />
                            </InputGroup>
                        </div>

                        {/*<Switch*/}
                        {/*    checked={checkboxChecked}*/}
                        {/*    onChange={onCheckboxChange}*/}
                        {/*    label={t('expenses.show_reconsiled')}*/}
                        {/*/>*/}
                    </div>
                </div>
            </StyledReport>
            <Scrollbar
                style={{ width: '100%', height: 'calc(100vh - 150px)' }}
                trackYProps={{
                    renderer: props => {
                        const { elementRef, ...restProps } = props;
                        return <div {...restProps} ref={elementRef} className="scroll-track" />;
                    }
                }}
                thumbYProps={{
                    renderer: props => {
                        const { elementRef, ...restProps } = props;
                        return <div {...restProps} ref={elementRef} className="scroll-thumb" />;
                    }
                }}
                rtl={false}
                noScrollX
            >
                <div id="section-to-print" ref={pdfContainer} className='balance-container'>
                    <div className='account-info'>
                        <span className='styled-title'>{t('reports.opening_account')}</span>
                        <Input value={openBankAmount} onChange={e => setOpenBankAmount(e.target.value)}/>
                        {/*<Button className='update-btn' rounded={false}>{t('searchPayments.btns.update')} <img className='refresh' src={refresh}/></Button>*/}
                    </div>
                    <div className='actual-balance'>{t('reports.actual_balance')} {firstMonth}</div>
                    {/* Income */}
                    <div className='balance-records-block'>
                        <div className='styled-title'>{t('expenses.income')}</div>
                        {renderColumnTitles(true)}
                        <div className='balance-records'>
                            {incomeRecords?.map((record, i) => {
                                return (
                                    <BalanceReportCard
                                        key={i}
                                        currency={record.currency}
                                        charged={record.paid}
                                        needed={record.total}
                                        expenseKey={record.incomeType}
                                        year={toYear}
                                        ltr={ltr}
                                    />
                                )
                            })}
                            {!!totalRecords?.income?.value && (
                                <BalanceReportCard
                                    total={true}
                                    currency={totalRecords?.income?.currency}
                                    charged={totalRecords?.income?.paid}
                                    needed={totalRecords?.income?.value}
                                    methods={totalRecords?.income?.methods}
                                    ltr={ltr}
                                />
                            )}
                        </div>
                    </div>
                    {/* Expense */}
                    <div className='balance-records-block'>
                        <div className='styled-title'>{t('expenses.expenses')}</div>
                        {renderColumnTitles()}
                        <div className='balance-records'>
                            {expenseRecords?.map((record, i) => {
                                return (
                                    <BalanceReportCard
                                        key={i}
                                        currency={record.currency}
                                        charged={record.paid}
                                        needed={record.amount}
                                        expenseKey={record.expenseTypeKey}
                                        ltr={ltr}
                                        expense
                                    />
                                )
                            })}
                            {!!totalRecords?.expense?.value && (
                                <BalanceReportCard
                                    total={true}
                                    currency={totalRecords?.expense?.currency}
                                    charged={totalRecords?.expense?.paid}
                                    needed={totalRecords?.expense?.value}
                                    methods={totalRecords?.expense?.methods}
                                    ltr={ltr}
                                    expense
                                />
                            )}
                        </div>
                    </div>
                    <div className='total-balance'>
                        <div className='bold-row'>
                            <span className='styled-title'>{t('expenses.total_balance')}</span>
                            <span>{roundValues((Number(openBankAmount) || 0) + totalRecords?.income?.paid - totalRecords?.expense?.paid)} <Currency>{totalRecords?.expense?.currency}</Currency></span>
                        </div>
                        <div className='actual-balance'>{t('expenses.count_balance')}</div>
                    </div>
                </div>
            </Scrollbar>
        </StyledBalanceReport>
    );
};

export default BalanceReport;
